import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';

import 'katex/dist/katex.min.css';

import styles from './MarkdownRenderer.module.css';

export interface MarkdownRendererProps {
  /**
   * Element overrides merged over the defaults below. The help site uses this
   * to resolve images to captured screenshots and keep internal links in the
   * app; chat passes nothing and renders as it always has.
   */
  components?: Partial<Components>;
  content: string;
}

function isWhitespaceOnly(value: string) {
  return value.trim().length === 0;
}

// remark-math treats a single $ as an inline-math delimiter, so "$5 plus $3"
// would render "5 plus " as an equation. Kudu's delimiter is $$ on both sides,
// so every $ that is not part of a $$ pair is escaped before remark-math sees
// it. Escaping (rather than deleting) keeps the character visible to the reader.
function escapeStrayDollars(text: string) {
  return text.replace(/\$/g, '\\$');
}

export function normalizeKuduMathDelimiters(markdown: string) {
  if (!markdown.includes('$$')) {
    // No math at all: every $ present is literal.
    return escapeStrayDollars(markdown);
  }

  let result = '';
  let cursor = 0;

  while (cursor < markdown.length) {
    const start = markdown.indexOf('$$', cursor);

    if (start === -1) {
      result += escapeStrayDollars(markdown.slice(cursor));
      break;
    }

    const end = markdown.indexOf('$$', start + 2);

    if (end === -1) {
      // Unterminated: the remainder is literal, opening $$ included.
      result += escapeStrayDollars(markdown.slice(cursor));
      break;
    }

    const inner = markdown.slice(start + 2, end);
    const previousLineBreak = markdown.lastIndexOf('\n', start - 1);
    const nextLineBreak = markdown.indexOf('\n', end + 2);
    const beforeMatch = markdown.slice(previousLineBreak + 1, start);
    const afterMatch = markdown.slice(
      end + 2,
      nextLineBreak === -1 ? markdown.length : nextLineBreak,
    );
    const isDisplay =
      inner.includes('\n') ||
      (isWhitespaceOnly(beforeMatch) && isWhitespaceOnly(afterMatch));

    result += escapeStrayDollars(markdown.slice(cursor, start));
    // The math body is passed through untouched: a $ inside an equation is
    // LaTeX, not currency.
    result += isDisplay ? `$$${inner}$$` : `$${inner}$`;
    cursor = end + 2;
  }

  return result;
}

const defaultComponents: Partial<Components> = {
  a: ({ children, ...props }) => (
    <a
      {...props}
      className={styles.link}
      rel="noreferrer"
      target="_blank"
    >
      {children}
    </a>
  ),
  code: ({ children, className, ...props }) => {
    const isBlock = Boolean(className);

    if (isBlock) {
      return (
        <code {...props} className={`${styles.codeBlock} ${className}`}>
          {children}
        </code>
      );
    }

    return (
      <code {...props} className={styles.inlineCode}>
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className={styles.pre}>{children}</pre>,
};

export function MarkdownRenderer({ components, content }: MarkdownRendererProps) {
  return (
    <div className={styles.renderer}>
      <ReactMarkdown
        components={{ ...defaultComponents, ...components }}
        rehypePlugins={[rehypeKatex]}
        remarkPlugins={[remarkMath]}
        skipHtml
      >
        {normalizeKuduMathDelimiters(content)}
      </ReactMarkdown>
    </div>
  );
}
